import React, { useState, useEffect } from 'react';
import { X, CreditCard, DollarSign, AlertCircle, AlertTriangle, Lock } from 'lucide-react';
import { toast } from 'react-toastify';
import usePaymentSettings from '../hooks/usePaymentSettings';

const PartialPaymentModal = ({ isOpen, onClose, appointment, onSuccess }) => {
  const {
    allowsDownPayment,
    computeDownPaymentAmount,
    adminCanOverrideDownPayment,
    downPaymentNotice,
    downPaymentType,
    downPaymentPercentage,
  } = usePaymentSettings();

  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [transactionId, setTransactionId] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  // Compute the suggested down-payment amount for this appointment so the
  // modal can pre-fill it (or lock it, when override is disabled).
  const totalPrice = parseFloat(appointment?.total_price || 0);
  const amountPaid = parseFloat(appointment?.amount_paid || 0);
  const remainingBalance = totalPrice - amountPaid;
  const suggestedDownPayment = allowsDownPayment
    ? Math.min(computeDownPaymentAmount(totalPrice), remainingBalance)
    : 0;

  // "Down-payment mode" = this appointment hasn't been paid against yet AND
  // the business has down payments enabled. In that mode the modal acts as
  // the down-payment recorder; the field locks if the admin doesn't have
  // override permission. Otherwise the modal behaves as a generic
  // after-the-fact partial-payment recorder (any amount up to remaining).
  const isDownPaymentMode = allowsDownPayment && amountPaid === 0 && suggestedDownPayment > 0;
  const lockToSuggested = isDownPaymentMode && !adminCanOverrideDownPayment;

  useEffect(() => {
    if (isOpen && appointment) {
      // Pre-fill with the suggested down-payment amount in down-payment mode
      // so admins don't have to retype the configured default. In after-the-
      // fact partial-payment mode, leave blank for free entry.
      setAmount(isDownPaymentMode ? suggestedDownPayment.toFixed(2) : '');
      setPaymentMethod('cash');
      setTransactionId('');
      setNotes('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, appointment, isDownPaymentMode, suggestedDownPayment]);

  if (!isOpen || !appointment) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const paymentAmount = parseFloat(amount);

    if (!paymentAmount || paymentAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (paymentAmount > remainingBalance) {
      toast.error(`Amount cannot exceed remaining balance of $${remainingBalance.toFixed(2)}`);
      return;
    }

    setLoading(true);

    try {
      await onSuccess({
        amount: paymentAmount,
        paymentMethod,
        transactionId: transactionId || null,
        notes: notes || null,
      });

      toast.success('Partial payment recorded');
      onClose();
    } catch (error) {
      console.error('Partial payment error:', error);
      toast.error('Failed to record partial payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          isDownPaymentMode ? 'bg-emerald-50' : 'bg-blue-50'
        }`}>
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${
              isDownPaymentMode ? 'bg-emerald-100' : 'bg-blue-100'
            }`}>
              <DollarSign className={isDownPaymentMode ? 'text-emerald-600' : 'text-blue-600'} size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {isDownPaymentMode ? 'Record Down Payment' : 'Record Partial Payment'}
              </h2>
              <p className="text-sm text-gray-600">
                Appointment #{appointment.id?.slice(0, 8)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Payment Summary */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total Price:</span>
              <span className="font-semibold">${totalPrice.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Already Paid:</span>
              <span className="font-semibold text-green-600">${amountPaid.toFixed(2)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-gray-200">
              <span className="font-semibold text-gray-900">Remaining Balance:</span>
              <span className="text-xl font-bold text-primary-600">
                ${remainingBalance.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Down-payment mode info banner */}
          {isDownPaymentMode && (
            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-900">
              <div className="font-semibold mb-1">
                Suggested down payment: ${suggestedDownPayment.toFixed(2)}
                {downPaymentType === 'percent' && (
                  <span className="font-normal text-emerald-700"> ({downPaymentPercentage}% of ${totalPrice.toFixed(2)})</span>
                )}
              </div>
              {lockToSuggested ? (
                <div className="flex items-start space-x-2 text-xs">
                  <Lock size={14} className="flex-shrink-0 mt-0.5" />
                  <span>
                    The amount is locked to the configured default. To override
                    the down-payment amount per appointment, enable
                    <em> "Let admin set the down-payment amount per appointment" </em>
                    in Payment Settings.
                  </span>
                </div>
              ) : (
                <div className="text-xs">
                  You can adjust the amount below before recording. Override is
                  enabled in Payment Settings.
                </div>
              )}
            </div>
          )}

          {/* Non-refundable notice (what the client sees) */}
          {isDownPaymentMode && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
                <div className="text-sm text-amber-900">
                  <div className="font-semibold mb-1">Non-refundable notice (shown to client)</div>
                  <div className="text-xs">{downPaymentNotice}</div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Amount */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Amount *
              {lockToSuggested && (
                <span className="ml-2 inline-flex items-center text-xs text-emerald-700 font-normal">
                  <Lock size={12} className="mr-1" /> locked
                </span>
              )}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                $
              </span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={remainingBalance}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={`input pl-8 ${lockToSuggested ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder="0.00"
                required
                readOnly={lockToSuggested}
              />
            </div>
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-gray-500">
                {lockToSuggested
                  ? `Locked to suggested down payment.`
                  : `Enter any amount up to $${remainingBalance.toFixed(2)}`}
              </p>
              {isDownPaymentMode && !lockToSuggested && parseFloat(amount) !== suggestedDownPayment && (
                <button
                  type="button"
                  onClick={() => setAmount(suggestedDownPayment.toFixed(2))}
                  className="text-xs text-emerald-700 hover:text-emerald-900 font-medium"
                >
                  Use suggested (${suggestedDownPayment.toFixed(2)})
                </button>
              )}
            </div>
          </div>

          {/* Payment Method */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Method *
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="input"
              required
            >
              <option value="cash">Cash</option>
              <option value="check">Check</option>
              <option value="debit">Debit Card</option>
              <option value="credit">Credit Card</option>
              <option value="stripe">Stripe</option>
              <option value="paypal">PayPal</option>
              <option value="square">Square</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Transaction ID */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Transaction/Reference ID
              <span className="text-gray-500 text-xs ml-1">(Optional)</span>
            </label>
            <input
              type="text"
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              className="input"
              placeholder="e.g., Check #1234, Last 4 digits, etc."
            />
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
              <span className="text-gray-500 text-xs ml-1">(Optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="input"
              rows="2"
              placeholder="Any additional notes about this payment..."
            />
          </div>

          {/* Info Box */}
          <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
              <p className="text-sm text-blue-800">
                {isDownPaymentMode
                  ? 'This will record the down payment. The appointment will be marked as "partial" until the remaining balance is paid at the appointment.'
                  : 'This will record a partial payment. The appointment will remain marked as "partial" until the full balance is paid.'}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 btn btn-primary"
            >
              {loading ? 'Recording...' : (isDownPaymentMode ? 'Record Down Payment' : 'Record Payment')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PartialPaymentModal;
